import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../shared/formatMoney';
import OwnerNav from './OwnerNav';
import { HiExclamationCircle } from 'react-icons/hi';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const OwnerTransactions = () => {
    const { user } = useAuth();
    const ownerCode = user?.linked_entity_code;

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const [dateFrom, setDateFrom] = useState(firstDay.toISOString().split('T')[0]);
    const [dateTo, setDateTo] = useState(today.toISOString().split('T')[0]);
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = async () => {
        if (!ownerCode) return;
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${rootUrl}/api/owners/${ownerCode}/transactions`, {
                params: { date_from: dateFrom, date_to: dateTo }
            });
            setTxns(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load transactions.');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    if (!ownerCode) return (
        <div className="min-h-screen flex items-center justify-center text-gray-500 pb-20">
            No owner account linked.
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="bg-orange-500 px-4 pt-10 pb-6">
                <h1 className="text-white text-xl font-bold">Transactions</h1>
            </div>

            <div className="px-4 py-4 space-y-3">
                <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">From</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={e => setDateFrom(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">To</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="w-full bg-orange-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
                    >
                        Go
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
                        <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : txns.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-400 text-sm">
                        No transactions for this period.
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <ul className="divide-y divide-gray-50">
                            {txns.map((txn, i) => (
                                <li key={txn.id || i} className="px-4 py-3 flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800">{txn.trans_type || txn.type}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{fmt(txn.date || txn.trans_date)}</p>
                                        {txn.plate_no && (
                                            <p className="text-xs text-gray-500 mt-0.5">{txn.plate_no}</p>
                                        )}
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className={`text-sm font-semibold ${Number(txn.amount) < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                                            {formatMoney(txn.amount)}
                                        </p>
                                        {txn.balance !== undefined && (
                                            <p className="text-xs text-gray-400 mt-0.5">Bal: {formatMoney(txn.balance)}</p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <OwnerNav />
        </div>
    );
};

export default OwnerTransactions;
